import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShippingOutlined as ShippedIcon,
  Done as DeliveredIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// N11 Order interface
interface N11OrderItem {
  productId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  n11DueAmount?: number;
  n11Price?: number;
  n11StockCode?: string;
  n11LineId?: number;
  n11OrderLineId?: number;
}

interface N11Address {
  address: string;
  city: string;
  district: string;
  fullName: string;
  gsm: string;
  neighborhood?: string;
  postalCode: string;
  tcId?: string;
  invoiceType?: number;
  taxId?: string;
  taxHouse?: string;
}

interface N11CargoInfo {
  cargoProviderName?: string;
  cargoSenderNumber?: string;
  cargoTrackingLink?: string;
  cargoTrackingNumber?: string;
  shipmentCompanyId?: number;
  shipmentMethod?: number;
}

interface N11PackageHistory {
  createdDate: number;
  status: string;
}

interface N11Order {
  id: string;
  orderId: string;
  orderNumber: string;
  date: string;
  status: 'paid' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'cancel_requested';
  totalAmount: number;
  itemsTotal?: number;
  items: N11OrderItem[];
  shippingAddress?: N11Address;
  billingAddress?: N11Address;
  cargoInfo?: N11CargoInfo;
  packageHistories?: N11PackageHistory[];
  shipmentPackageStatus?: string;
  // N11 özel alanları
  marketplace: string;
  n11OrderId?: string;
  n11OrderNumber?: string;
  n11Status?: string;
  n11LastModified?: number;
  customerName?: string;
  customerEmail?: string;
  importedAt?: string;
}

interface N11OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: N11Order | null;
  getStatusChip: (status: string) => React.ReactElement;
}

const N11OrderDetailsModal: React.FC<N11OrderDetailsModalProps> = ({ 
  open, 
  onClose, 
  order, 
  getStatusChip 
}) => {
  const { t } = useTranslation('salesSystem');
  if (!order) return null;

  // Paket durumu ikonunu al
  const getPackageStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'created':
        return <CheckCircleIcon sx={{ color: '#2196f3' }} />;
      case 'picking':
        return <CheckCircleIcon sx={{ color: '#ff9800' }} />;
      case 'shipped':
        return <ShippedIcon sx={{ color: '#4caf50' }} />;
      case 'delivered':
        return <DeliveredIcon sx={{ color: '#4caf50' }} />;
      case 'cancelled':
        return <CancelIcon sx={{ color: '#f44336' }} />;
      default:
        return <CheckCircleIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  // Paket durumu rengini al
  const getPackageStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'created':
        return '#2196f3';
      case 'picking':
        return '#ff9800';
      case 'shipped':
        return '#4caf50';
      case 'delivered':
        return '#4caf50';
      case 'cancelled':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  // Package status text
  const getPackageStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'created':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderCreated');
      case 'picking':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderConfirmed');
      case 'shipped':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderShipped');
      case 'delivered':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderDelivered');
      case 'cancelled':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderCancelled');
      default:
        return status;
    }
  };

  const getN11ShipmentStatusText = (status?: string) => {
    if (!status) {
      return t('ecommerceOrders.marketplaces.unknown');
    }

    switch (status.toLowerCase()) {
      case 'unknown':
        return t('ecommerceOrders.marketplaces.unknown');
      case 'created':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderCreated');
      case 'picking':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderConfirmed');
      case 'shipped':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderShipped');
      case 'delivered':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderDelivered');
      case 'cancelled':
        return t('ecommerceOrders.n11OrderDetailsModal.packageStatus.orderCancelled');
      default:
        return status;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {/* N11 Logo - En Üst */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          mb: 2
        }}>
          <img 
            src="/assets/logoHighResNew.png" 
            alt="Logo"
            style={{ 
              height: '40px',
              width: 'auto'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: '#f3e5f5', 
            px: 1, 
            py: 0.5, 
            borderRadius: 1,
            border: '1px solid #7b1fa2'
          }}>
            <StoreIcon sx={{ color: '#7b1fa2', mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
              N11
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6">
              {t('ecommerceOrders.n11OrderDetailsModal.title', { orderNumber: order.n11OrderNumber || order.orderNumber })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customerName || t('ecommerceOrders.table.customerNameUnknown')} - {order.customerEmail || t('ecommerceOrders.table.emailUnknown')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* N11 Sipariş Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.n11OrderDetailsModal.orderInformation')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.n11OrderNo')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {order.n11OrderNumber || order.orderNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.n11OrderId')}:</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {order.n11OrderId || order.orderId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.date')}:</Typography>
                    <Typography variant="body2">
                      {new Date(order.date).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.systemStatus')}:</Typography>
                    {getStatusChip(order.status)}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.n11ShipmentStatus')}:</Typography>
                    <Chip 
                      label={getN11ShipmentStatusText(order.shipmentPackageStatus)} 
                      size="small"
                      sx={{ 
                        bgcolor: '#f3e5f5', 
                        color: '#7b1fa2',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                  {order.importedAt && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.imported')}:</Typography>
                      <Typography variant="body2">
                        {new Date(order.importedAt).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fiyat Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.n11OrderDetailsModal.priceInformation')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.productsTotal')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{(order.itemsTotal || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.shippingFee')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{(order.totalAmount - (order.itemsTotal || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold">{t('ecommerceOrders.orderDetailsModal.totalAmount')}:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ₺{order.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Kargo Bilgileri */}
          {order.cargoInfo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <LocalShippingIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.n11OrderDetailsModal.cargoInformation')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.cargoCompany')}:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {order.cargoInfo.cargoProviderName || t('ecommerceOrders.n11OrderDetailsModal.notSpecified')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.trackingNumber')}:</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {order.cargoInfo.cargoTrackingNumber || t('ecommerceOrders.n11OrderDetailsModal.none')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.cargoCode')}:</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {order.cargoInfo.cargoSenderNumber || t('ecommerceOrders.n11OrderDetailsModal.none')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.shipmentMethod')}:</Typography>
                      <Typography variant="body2">
                        {order.cargoInfo.shipmentMethod === 1 ? t('ecommerceOrders.n11OrderDetailsModal.cargo') : t('ecommerceOrders.orderDetailsModal.other')}
                      </Typography>
                    </Box>
                    {order.cargoInfo.cargoTrackingLink && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.n11OrderDetailsModal.trackingLink')}:</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(order.cargoInfo?.cargoTrackingLink, '_blank')}
                          sx={{ 
                            fontSize: '0.75rem',
                            color: '#7b1fa2',
                            borderColor: '#7b1fa2'
                          }}
                        >
                          {t('ecommerceOrders.n11OrderDetailsModal.track')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Paket Geçmişi */}
          {order.packageHistories && order.packageHistories.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <TimelineIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.n11OrderDetailsModal.packageHistory')}
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {order.packageHistories.map((history, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: getPackageStatusColor(history.status),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {getPackageStatusIcon(history.status)}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="medium">
                              {getPackageStatusText(history.status)}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {new Date(history.createdDate).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Teslimat Adresi */}
          {order.shippingAddress && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <LocationOnIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.orderDetailsModal.deliveryAddress')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {order.shippingAddress.fullName}
                    </Typography>
                    <Typography variant="body2">
                      {order.shippingAddress.address}
                    </Typography>
                    <Typography variant="body2">
                      {order.shippingAddress.neighborhood && `${order.shippingAddress.neighborhood}, `}
                      {order.shippingAddress.district}, {order.shippingAddress.city}
                    </Typography>
                    <Typography variant="body2">
                      {t('ecommerceOrders.orderDetailsModal.postalCode')}: {order.shippingAddress.postalCode}
                    </Typography>
                    <Typography variant="body2">
                      {t('ecommerceOrders.n11OrderDetailsModal.phone')}: {order.shippingAddress.gsm}
                    </Typography>
                    {order.shippingAddress.tcId && (
                      <Typography variant="body2" color="text.secondary">
                        TC: {order.shippingAddress.tcId}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Fatura Adresi */}
          {order.billingAddress && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <ReceiptIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.n11OrderDetailsModal.billingAddress')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {order.billingAddress.fullName}
                    </Typography>
                    <Typography variant="body2">
                      {order.billingAddress.address}
                    </Typography>
                    <Typography variant="body2">
                      {order.billingAddress.neighborhood && `${order.billingAddress.neighborhood}, `}
                      {order.billingAddress.district}, {order.billingAddress.city}
                    </Typography>
                    <Typography variant="body2">
                      {t('ecommerceOrders.orderDetailsModal.postalCode')}: {order.billingAddress.postalCode}
                    </Typography>
                    <Typography variant="body2">
                      {t('ecommerceOrders.n11OrderDetailsModal.phone')}: {order.billingAddress.gsm}
                    </Typography>
                    <Typography variant="body2">
                      {t('ecommerceOrders.n11OrderDetailsModal.invoiceType')}: {order.billingAddress.invoiceType === 1 ? t('ecommerceOrders.n11OrderDetailsModal.individual') : t('ecommerceOrders.n11OrderDetailsModal.corporate')}
                    </Typography>
                    {order.billingAddress.tcId && (
                      <Typography variant="body2" color="text.secondary">
                        TC: {order.billingAddress.tcId}
                      </Typography>
                    )}
                    {order.billingAddress.taxId && (
                      <Typography variant="body2" color="text.secondary">
                        {t('ecommerceOrders.n11OrderDetailsModal.taxNo')}: {order.billingAddress.taxId}
                      </Typography>
                    )}
                    {order.billingAddress.taxHouse && (
                      <Typography variant="body2" color="text.secondary">
                        {t('ecommerceOrders.n11OrderDetailsModal.taxOffice')}: {order.billingAddress.taxHouse}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Sipariş Edilen Ürünler */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.n11OrderDetailsModal.orderedProducts', { count: order.items.length })}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {order.items.map((item, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={item.image}
                          alt={item.name}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('ecommerceOrders.n11OrderDetailsModal.n11StockCode')}: {item.n11StockCode || t('ecommerceOrders.n11OrderDetailsModal.none')}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {t('ecommerceOrders.n11OrderDetailsModal.n11LineId')}: {item.n11LineId || item.n11OrderLineId || t('ecommerceOrders.n11OrderDetailsModal.none')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {t('ecommerceOrders.orderDetailsModal.quantity')}: {item.quantity}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                          <Typography variant="body2" fontWeight="medium">
                            ₺{item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          {item.n11Price && item.n11Price !== item.price && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {t('ecommerceOrders.n11OrderDetailsModal.n11Price')}: ₺{item.n11Price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </Typography>
                          )}
                          <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 'bold' }}>
                            {t('ecommerceOrders.orderDetailsModal.total')}: ₺{(item.price * item.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('ecommerceOrders.orderDetailsModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default N11OrderDetailsModal;
